import i18n from 'i18next'
import { mapValues } from 'lodash'
import type { KeyValuePair, ManCode } from '#api/tmsApiService'
import { UntitledConfigurationPlaceholder } from '#features/Configuration/ConfigurationTitle'
import type { ConfigurationProductFamilyV1 } from '#features/ManCode/ConfigurationStorageDataV1'
import { type ConfigurationStorageDataV2, isConfigurationStorageDataV2, type OptionChangeEntryV2 } from '#features/ManCode/ConfigurationStorageDataV2'
import { readInternalStoredData } from '#features/ManCode/readInternalStoredData'
import { toaster } from '#features/Toasts/toaster'
import type { ConfigurationVersion, LocalConfigurationId, TruckConfiguration } from '#store/slices/configurationSlice'
import { log } from '#utils/logger'

export function mapTruckConfigurationToManCodeService(configuration: TruckConfiguration): KeyValuePair {
  const data: ConfigurationStorageDataV2 = {
    type: 'tms-frontend-truck-configuration',
    version: '0.2.0',
    configurationVersion: configuration.version,
    title: configuration.title,
    configuredFamilies: mapValues(configuration.configuredFamilies, (value) => {
      if (!value) {
        throw new Error('Unexpected undefined value in configuredFamilies')
      }

      return {
        selectedVariant: value.selectedVariant,
        isLocked: value.isLocked,
        printOptions: value.printOptions,
      } satisfies ConfigurationProductFamilyV1
    }),
    history: configuration.history
      ?.filter((e) => e.type === 'change')
      ?.filter((e) => e.status === 'success')
      ?.filter((e) => {
        // check if after all undo/redo actions the element still exists
        const { process } = e
        const undos = configuration.history?.filter(
          (entry) => entry.type === 'undo' && (entry.status === 'pending' || entry.status === 'success') && entry.sourceChange === process,
        )
        const redos = configuration.history?.filter(
          (entry) => entry.type === 'redo' && (entry.status === 'pending' || entry.status === 'success') && entry.sourceChange === process,
        )
        // no undos, so the element stays persistent
        if (!undos || undos.length <= 0) {
          return true
        }
        // either no redos (so it has been undone) or there more-equal redos, thus the element stays.
        return redos && undos.length <= redos.length
      })
      .map((entry) => {
        return {
          process: entry.process,
          started: entry.started,
          type: entry.type,
          config: entry.config,
          selections: entry.selections,
          // deselections are omitted, they're hidden anyway
          optionSource: entry.optionSource || 'ecm',
          status: 'success',
          ended: entry.ended,
          conflicts: [],
          consequences: entry.consequences,
        } satisfies OptionChangeEntryV2
      }),
    initialSelection: configuration.initialSelection,
    checks: configuration.checks,
    optionalEquipment: configuration.optionalEquipment,
  }

  return {
    key: 'tms-frontend-truck-configuration',
    value: JSON.stringify(data),
  }
}

export type StoredPartialConfiguration = Pick<
  TruckConfiguration,
  'configuredFamilies' | 'history' | 'initialSelection' | 'title' | 'version' | 'checks' | 'optionalEquipment' | 'marketDenomination'
>

export function mapManCodeToTruckConfiguration(manCode: ManCode, configurationId: LocalConfigurationId): StoredPartialConfiguration | undefined {
  const parsedData = readInternalStoredData(manCode)

  if (!isConfigurationStorageDataV2(parsedData)) {
    toaster.add({
      appearance: 'warning',
      visibleInMs: 10000,
      title: i18n.t(
        'configuration.messages.invalid-configuration-data',
        'Unexpected configuration data format, some configuration data could not be restored',
      ),
    })
    log.warn('Unexpected configuration data format, some configuration data could not be restored', parsedData)
    return
  }

  log.log('Read configuration from manCode: ', parsedData, 'manCode: ', manCode)

  return {
    version: (parsedData.configurationVersion ?? 1) as ConfigurationVersion,
    title: parsedData.title ?? UntitledConfigurationPlaceholder,
    marketDenomination: manCode.configuration?.vehicleDescription,
    configuredFamilies: parsedData.configuredFamilies,
    // history entries have stored their old configuration ID which is now invalid
    history: parsedData.history?.filter((x) => x.type === 'change').map((x) => ({ ...x, deselections: [], config: configurationId })),
    initialSelection: parsedData.initialSelection,
    checks: parsedData.checks ?? {},
    optionalEquipment: parsedData.optionalEquipment ?? [],
  } satisfies StoredPartialConfiguration
}
